import React from "react";
import { CATEGORIES, HOME_PROJECTS } from "./works-projects.js";
import { S, openConsult } from "../site.js";

/* Home "Our works" preview.
   Lanes derive from the shared roster in works-projects.js
   (CATEGORIES + HOME_PROJECTS) so the home preview can never
   desync from the full studio wall at /works. Each lane's count,
   featured card, and remainder list come straight from the roster;
   the visual tokens come from CATEGORIES. Featured = the project
   flagged featured:true in that category, else the first one. */
const discussProject = openConsult({ source: "works" });
const { useState: useState_WP, useEffect: useEffect_WP, useRef: useRef_WP } = React;

function buildWorksLanes(projects) {
  return CATEGORIES.filter(c => projects.some(p => p.cat === c.id)).map((c) => {
    const inCat = projects.filter(p => p.cat === c.id);
    const featured = inCat.find(p => p.featured) || inCat[0];
    return {
      id: c.id, label: c.label, short: c.short, hue: c.hue,
      accent: c.accent, soft: c.soft, deep: c.deep,
      count: inCat.length,
      featured,
      rest: inCat.filter(p => p !== featured),
      hasRepresentative: inCat.some(p => p.representative),
    };
  });
}

function WorksPreview() {
  const lanes = buildWorksLanes(HOME_PROJECTS);
  const [active, setActive] = useState_WP(lanes[0] ? lanes[0].id : "ai");
  const laneRailRef = useRef_WP(null);
  const laneRefs = useRef_WP({});
  const lane = lanes.find(l => l.id === active) || lanes[0];

  // Mobile: the lane rail is a horizontal snap carousel, so centre the
  // active chip after a tap. Horizontal only — never scrolls the page.
  useEffect_WP(() => {
    const rail = laneRailRef.current;
    const item = laneRefs.current[active];
    if (!rail || !item || window.innerWidth > 600) return;
    const left = item.offsetLeft - (rail.clientWidth - item.offsetWidth) / 2;
    rail.scrollTo({ left: Math.max(0, left), behavior: "smooth" });
  }, [active]);

  if (!lane) return null;

  const { featured } = lane;
  const hasOutcome = featured.metric && featured.metric !== "—";

  return (
    <section className="section-pad" id="works" style={{ background: "var(--paper)", position: "relative" }}>
      <div className="container">
        <div className="section-header" style={{ maxWidth: 820 }}>
          <div className="eyebrow">Our works</div>
          <h2>
            Selected work across{" "}
            <span style={{ color: "var(--indigo)" }}>AI, products, systems, and AI-ready websites.</span>
          </h2>
          <p className="lead">
            AI workflows, MVPs, business systems, mobile apps, and modern websites — selected from what we design and build for clients.
          </p>
          <div className="works-proof-row">
            <span className="mono">4 build practices</span>
            <span className="mono">Myanmar + Japan</span>
            <span className="mono">Senior-led engagements</span>
          </div>
        </div>

        <div className="works-preview-grid">
          {/* Practice index — doubles as the tab strip for the panel beside it. */}
          <div
            className="works-preview-lanes mobile-snap-rail"
            ref={laneRailRef}
            role="tablist"
            aria-label="Practices"
          >
            {lanes.map((l, i) => {
              const on = l.id === active;
              return (
                <button
                  key={l.id}
                  ref={(node) => { laneRefs.current[l.id] = node; }}
                  onClick={() => setActive(l.id)}
                  className="works-lane-row"
                  role="tab"
                  id={`works-tab-${l.id}`}
                  aria-selected={on}
                  aria-controls={`works-panel-${l.id}`}
                  data-active={on}
                  style={{ "--lane-accent": l.accent, "--lane-soft": l.soft, "--lane-deep": l.deep }}
                >
                  <span className="mono works-lane-no">0{i + 1}</span>
                  <span className="works-lane-label">{l.label}</span>
                  <span className="mono works-lane-count">{l.count} project{l.count === 1 ? "" : "s"}</span>
                  <span aria-hidden="true" className="works-lane-arrow">→</span>
                </button>
              );
            })}
          </div>

          {/* ponytail: buttons + aria, no roving tabindex. Every lane is
              reachable by Tab already; arrow-key roving is the upgrade
              path if the strip ever grows past a handful of practices. */}
          <div
            key={lane.id}
            className="works-panel lift-in"
            role="tabpanel"
            id={`works-panel-${lane.id}`}
            aria-labelledby={`works-tab-${lane.id}`}
            tabIndex={-1}
            style={{ "--lane-accent": lane.accent, "--lane-soft": lane.soft, "--lane-deep": lane.deep }}
          >
            <article className="works-card">
              {/* Photo plate when the project ships mockups; flat tinted
                  panel otherwise, reading as deliberate, not missing. */}
              <div className="works-card-plate" data-has-img={Boolean(featured.mockups)}>
                {featured.mockups ? (
                  <figure className="works-card-img">
                    {/* Phones get the portrait mobile shot (the plate goes
                        taller there via CSS), tablets the tablet shot,
                        desktop the laptop shot. */}
                    <picture>
                      <source media="(max-width: 600px)" srcSet={featured.mockups.mobile} />
                      <source media="(max-width: 900px)" srcSet={featured.mockups.tablet} />
                      <img src={featured.mockups.desktop} alt={`${featured.name} — project mockup`} loading="lazy" />
                    </picture>
                  </figure>
                ) : (
                  <>
                    <span className="tag works-card-tag">
                      <span aria-hidden="true" className="works-card-dot" />
                      {lane.label}
                    </span>
                    {featured.year && featured.year !== "—" ? (
                      <span className="mono works-card-year">{featured.year}</span>
                    ) : null}
                    <h3 className="serif works-card-name">{featured.name}</h3>
                  </>
                )}
              </div>

              <div className="works-card-body">
                <div className="works-card-sector">{featured.sector} · {featured.role}</div>
                <p className="works-card-blurb">{featured.blurb}</p>

                {hasOutcome ? (
                  <div className="works-card-outcome">
                    <span className="mono works-card-outcome-label">Outcome</span>
                    <span className="works-card-outcome-value">{featured.metric}</span>
                  </div>
                ) : null}

                <div className="works-card-actions">
                  <a href={S.nextStep} onClick={discussProject} className="btn btn-primary btn-arrow">
                    <span>Discuss a project</span>
                  </a>
                  {featured.url ? (
                    <a href={featured.url} target="_blank" rel="noopener noreferrer" className="works-link">
                      Visit live<span aria-hidden="true"> ↗</span>
                    </a>
                  ) : null}
                  <a href={S.works} className="works-link">
                    All works<span aria-hidden="true"> →</span>
                  </a>
                </div>
              </div>
            </article>

            {lane.rest.length > 0 ? (
              <ul className="works-more">
                {lane.rest.map(p => (
                  <li key={p.name} className="works-more-row">
                    <span className="works-more-name">{p.name}</span>
                    <span className="mono works-more-sector">{p.sector}</span>
                  </li>
                ))}
              </ul>
            ) : null}

            {lane.hasRepresentative ? (
              <p className="works-disclosure">
                Some entries are representative examples where client details cannot be disclosed.
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

export { WorksPreview };
